import React, { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useTranscription } from "../hooks/useTranscription";
import { useTranslation } from "react-i18next";

interface VoiceRecorderProps {
  language: string;
  onTranscriptionChange: (text: string, method: "voice" | "text") => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  initialText?: string;
  checkinToken?: string;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  language,
  onTranscriptionChange,
  onProcessingChange,
  initialText = "",
  checkinToken,
}) => {
  const { t } = useTranslation();
  const [useTextInput, setUseTextInput] = useState(false);
  const [textValue, setTextValue] = useState(initialText);

  const {
    isSupported,
    isConnecting,
    isListening,
    transcript,
    interimTranscript,
    error,
    startListening,
    stopListening,
    resetTranscript,
    setTranscript,
  } = useTranscription({ language, checkinToken });

  // Notify parent when transcription is mid-processing (has unfinalised text)
  React.useEffect(() => {
    onProcessingChange?.(isConnecting || !!interimTranscript);
  }, [isConnecting, interimTranscript, onProcessingChange]);

  // If speech not supported, default to text
  React.useEffect(() => {
    if (!isSupported) {
      setUseTextInput(true);
    }
  }, [isSupported]);

  // Initialize voice transcript from previous response so it's visible when navigating back
  React.useEffect(() => {
    if (initialText && !useTextInput) {
      setTranscript(initialText);
    }
    // Only run on mount (key={currentPromptIndex} forces remount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync transcript changes to parent
  React.useEffect(() => {
    if (!useTextInput && transcript) {
      onTranscriptionChange(transcript, "voice");
    }
  }, [transcript, useTextInput, onTranscriptionChange]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextValue(e.target.value);
    onTranscriptionChange(e.target.value, "text");
  };

  const handleToggleInput = () => {
    if (useTextInput) {
      // Switching to voice
      setUseTextInput(false);
      if (textValue) {
        setTranscript(textValue);
      }
    } else {
      // Switching to text
      if (isListening) stopListening();
      setUseTextInput(true);
      if (transcript) {
        setTextValue(transcript);
        onTranscriptionChange(transcript, "text");
      }
    }
  };

  const handleReset = () => {
    if (isListening) stopListening();
    resetTranscript();
    setTextValue("");
    onTranscriptionChange("", useTextInput ? "text" : "voice");
  };

  if (useTextInput) {
    return (
      <div className="space-y-3">
        <Textarea
          value={textValue}
          onChange={handleTextChange}
          placeholder={t("checkin.typeHere")}
          className="min-h-[120px] resize-none"
        />
        <div className="flex items-center justify-between">
          {isSupported && (
            <button
              type="button"
              onClick={handleToggleInput}
              className="text-sm text-[#00c4cc] hover:text-[#00adc0] underline"
            >
              {t("checkin.switchToVoice")}
            </button>
          )}
          {textValue && (
            <button
              type="button"
              onClick={handleReset}
              className="text-sm text-gray-400 hover:text-gray-600"
            >
              {t("checkin.clear")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Mic button */}
      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={isListening ? stopListening : startListening}
          disabled={isConnecting}
          className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
            isConnecting
              ? "bg-[#00c4cc]/80 shadow-lg shadow-[#00c4cc]/20 animate-pulse"
              : isListening
                ? "bg-red-500 hover:bg-red-600 animate-pulse shadow-lg shadow-red-200"
                : "bg-[#00c4cc] hover:bg-[#00adc0] shadow-lg shadow-[#00c4cc]/30"
          }`}
        >
          {isConnecting ? (
            <div className="w-8 h-8 rounded-full animate-spin" style={{ border: "3px solid rgba(255,255,255,0.3)", borderTopColor: "white" }} />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isListening ? (
                <>
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </>
              ) : (
                <>
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" x2="12" y1="19" y2="22" />
                </>
              )}
            </svg>
          )}
        </button>
        <span className="text-sm text-gray-500">
          {isConnecting
            ? t("checkin.connecting", "Connexion...")
            : isListening
              ? t("checkin.listening")
              : t("checkin.tapToSpeak")}
        </span>
      </div>

      {/* Transcription display */}
      {(transcript || interimTranscript) && (
        <div className="bg-gray-50 rounded-lg p-4 min-h-[80px]">
          <p className="text-gray-800">
            {transcript}
            {interimTranscript && (
              <span className="text-gray-400"> {interimTranscript}</span>
            )}
          </p>
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Controls */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleToggleInput}
          className="text-sm text-[#00c4cc] hover:text-[#00adc0] underline"
        >
          {t("checkin.switchToText")}
        </button>
        {transcript && (
          <button
            type="button"
            onClick={handleReset}
            className="text-sm text-gray-400 hover:text-gray-600"
          >
            {t("checkin.clear")}
          </button>
        )}
      </div>
    </div>
  );
};
